import React, { useState } from 'react';
import { I18n } from '@kineticdata/react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import {
  StaticTypeahead,
  Utils,
  C,
  addToast,
  addToastAlert,
} from '@kineticdata/bundle-common';
import { List } from 'immutable';

export const NewApprovalGroupModal = ({
  agreement,
  toggle,
  createApprovalTeam,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);

  return (
    <Modal isOpen={true} toggle={toggle}>
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>Create Approval Group</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <div className="form p-3">
          <div className="form-group required">
            <label htmlFor="approval-group-name" className="field-label">
              <I18n>Approval Group Name</I18n>
            </label>
            <input
              type="text"
              id="approval-group-name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          {name.trim() === 'Master' && (
            <em className="text-danger">
              <I18n>You cannot name an approval group 'Master'.</I18n>
            </em>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-success"
          type="button"
          disabled={!name || pending || name.trim() === 'Master'}
          onClick={() => {
            setPending(true);
            createApprovalTeam({
              agreement,
              name: name.trim(),
              success: submission => {
                if (typeof onSuccess === 'function') {
                  onSuccess(submission);
                }
                addToast('Approval group created successfully');
              },
              failure: error => {
                addToastAlert({
                  severity: 'danger',
                  title: 'Failed to create approval group',
                  message: error.message,
                });
              },
              complete: toggle,
            });
          }}
        >
          {!pending ? (
            <I18n>Create Approval Group</I18n>
          ) : (
            <>
              <span className="fa fa-fw fa-spinner fa-spin" />
              <I18n>Creating</I18n>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};

const ApprovalUserSuggestionContent = ({ suggestion }) => (
  <>
    <div className="large">
      {suggestion.get('displayName') || suggestion.get('username')}
    </div>
    <div className="small">{suggestion.get('email')}</div>
  </>
);

export const NewApprovalManagerModal = ({
  agreement,
  toggle,
  addApprovalManager,
  isCustomerApprovalAdmin,
  options,
  existingUsernames = [],
}) => {
  const [user, setUser] = useState(null);
  const [master, setMaster] = useState(false);
  const [pending, setPending] = useState(false);

  return (
    <Modal isOpen={true} toggle={toggle}>
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>Add Approval Manager</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <div className="form p-3">
          <div className="form-group required">
            <label htmlFor="approval-manager" className="field-label">
              <I18n>Select User</I18n>
            </label>
            <StaticTypeahead
              valueProp="username"
              labelProp="displayName"
              options={(options || List())
                .filter(o => !existingUsernames.includes(o.User))
                .map(o => ({
                  username: o.User,
                  displayName:
                    [o['First Name'], o['Last Name']]
                      .filter(Boolean)
                      .join(' ') || o.Email,
                  email: o.Email,
                }))
                .sortBy(o => o.username)
                .toJS()}
              search={{
                fields: [
                  { name: 'displayName', operator: 'matches' },
                  { name: 'email', operator: 'matches' },
                  { name: 'username', operator: 'matches' },
                ],
              }}
              value={user}
              onChange={user => setUser(user ? user.toJS() : user)}
              minSearchLength={0}
              id="approval-manager"
              components={{ SuggestionContent: ApprovalUserSuggestionContent }}
            />
          </div>
          {isCustomerApprovalAdmin && (
            <div className="checkbox">
              <label htmlFor="master-manager">
                <input
                  type="checkbox"
                  id="master-manager"
                  checked={master}
                  onChange={e => setMaster(e.target.checked)}
                />
                <I18n>Make User a Master Approval Manager</I18n>
              </label>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-success"
          type="button"
          disabled={!user || pending}
          onClick={() => {
            setPending(true);
            addApprovalManager({
              teamName: agreement,
              username: user.username,
              master,
              success: submission => {
                addToast('Approval manager added successfully');
              },
              failure: error => {
                addToastAlert({
                  severity: 'danger',
                  title: 'Failed to add approval manager',
                  message: error.message,
                });
              },
              complete: toggle,
            });
          }}
        >
          {!pending ? (
            <I18n>Add Approval Manager</I18n>
          ) : (
            <>
              <span className="fa fa-fw fa-spinner fa-spin" />
              <I18n>Adding</I18n>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export const NewApproverModal = ({
  agreement,
  group,
  toggle,
  addApprover,
  options,
  existingUsernames = [],
}) => {
  const [user, setUser] = useState(null);
  const [pending, setPending] = useState(false);

  return (
    <Modal isOpen={true} toggle={toggle}>
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>Add Approver</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <div className="form p-3">
          <div className="form-group required">
            <label htmlFor="approver" className="field-label">
              <I18n>Select User</I18n>
            </label>
            <StaticTypeahead
              valueProp="username"
              labelProp="displayName"
              options={(options || List())
                .filter(o => !existingUsernames.includes(o.User))
                .map(o => ({
                  username: o.User,
                  displayName:
                    [o['First Name'], o['Last Name']]
                      .filter(Boolean)
                      .join(' ') || o.Email,
                  email: o.Email,
                }))
                .sortBy(o => o.username)
                .toJS()}
              search={{
                fields: [
                  { name: 'displayName', operator: 'matches' },
                  { name: 'email', operator: 'matches' },
                  { name: 'username', operator: 'matches' },
                ],
              }}
              value={user}
              onChange={user => setUser(user ? user.toJS() : user)}
              minSearchLength={0}
              id="approver"
              components={{ SuggestionContent: ApprovalUserSuggestionContent }}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-success"
          type="button"
          disabled={!user || pending}
          onClick={() => {
            setPending(true);
            addApprover({
              teamName: `${agreement}::${group}`,
              username: user.username,
              success: submission => {
                addToast('Approver added successfully');
              },
              failure: error => {
                addToastAlert({
                  severity: 'danger',
                  title: 'Failed to add approver',
                  message: error.message,
                });
              },
              complete: toggle,
            });
          }}
        >
          {!pending ? (
            <I18n>Add Approver</I18n>
          ) : (
            <>
              <span className="fa fa-fw fa-spinner fa-spin" />
              <I18n>Adding</I18n>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};

const ApprovalFormSuggestionContent = ({ suggestion }) => (
  <>
    <div className="large">
      {suggestion.get('name')}
      {!!suggestion.get('fundingDependent') ? ' [$]' : ''}
    </div>
    <div className="small">{suggestion.get('slug')}</div>
  </>
);

export const NewApprovalFormModal = ({
  agreement,
  group,
  toggle,
  addApprovalForm,
  options,
  existingForms = [],
}) => {
  const [form, setForm] = useState(null);
  const [type, setType] = useState('Standard');
  const [pending, setPending] = useState(false);

  return (
    <Modal isOpen={true} toggle={toggle}>
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>Add Service Offering</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <div className="form p-3">
          <div className="form-group required">
            <label htmlFor="approval-form" className="field-label">
              <I18n>Select Service Offering</I18n>
            </label>
            <StaticTypeahead
              valueProp="slug"
              labelProp="name"
              options={(options || List())
                .filter(o => !existingForms.includes(o.slug))
                .map(o => ({
                  name: o.name,
                  slug: o.slug,
                  fundingDependent: Utils.hasAttributeValue(
                    o,
                    C.FORM_ATTR_FUNDING_DEPENDENT,
                    'True',
                  ),
                }))
                .sortBy(o => o.name)
                .toJS()}
              search={{
                fields: [
                  { name: 'name', operator: 'matches' },
                  { name: 'slug', operator: 'matches' },
                ],
              }}
              value={form}
              onChange={form => setForm(form ? form.toJS() : form)}
              minSearchLength={0}
              id="approval-form"
              components={{ SuggestionContent: ApprovalFormSuggestionContent }}
            />
            {form &&
              form.fundingDependent && (
                <small>
                  <em>
                    <I18n>
                      This service offering requires funding to process.
                    </I18n>
                  </em>
                </small>
              )}
          </div>
          <div className="radio required">
            <label htmlFor="approval-type-radio" className="field-label">
              <I18n>Approval Type</I18n>
            </label>
            <label htmlFor="approval-type-public">
              <input
                name="approval-type-radio"
                type="radio"
                id="approval-type-public"
                checked={type === 'Standard'}
                onChange={() => setType('Standard')}
              />
              <I18n>Standard</I18n>
            </label>
            <label htmlFor="approval-type-management">
              <input
                name="approval-type-radio"
                type="radio"
                id="approval-type-management"
                checked={type === 'Management'}
                onChange={() => setType('Management')}
              />
              <I18n>Management</I18n>
            </label>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          className="btn btn-success"
          type="button"
          disabled={!form || !type || pending}
          onClick={() => {
            setPending(true);
            addApprovalForm({
              teamName: `${agreement}::${group}`,
              formSlug: form.slug,
              type,
              success: submission => {
                addToast('Service offering added successfully');
              },
              failure: error => {
                addToastAlert({
                  severity: 'danger',
                  title: 'Failed to add service offering',
                  message: error.message,
                });
              },
              complete: toggle,
            });
          }}
        >
          {!pending ? (
            <I18n>Add Service Offering</I18n>
          ) : (
            <>
              <span className="fa fa-fw fa-spinner fa-spin" />
              <I18n>Adding</I18n>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
};
